import { notify } from "@/components/bugsnag";
import { toast } from "@/shared/utils/toaster";
import { ToastType } from "@/shared/utils/toaster";
import {
  Button,
  cn,
  Flex,
  Text,
  TextEditor,
  TextEditorRef,
} from "@suns/design-system";
import { SparklesIcon } from "@suns/design-system/icons";
import { useEffect, useRef, useState } from "react";

interface AiEditorProps {
  title: string;
  defaultValue?: string;
  onGenerateAi?: () => Promise<string>;
  onSave?: (value: string) => Promise<boolean>;
  readonly?: boolean;
  className?: string;
  maxLength?: number;
  size?: "sm" | "md" | "lg";
  print?: boolean;
}

export function AiEditor({
  title,
  defaultValue,
  onGenerateAi,
  onSave,
  readonly = true,
  className,
  size = "md",
  maxLength = 600,
  print = false,
}: AiEditorProps) {
  const [aiLoading, setAiLoading] = useState(false);
  const editorRef = useRef<TextEditorRef>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedValue, setSavedValue] = useState(defaultValue);
  const [currentValue, setCurrentValue] = useState(defaultValue);

  useEffect(() => {
    setIsDirty(currentValue != savedValue);
  }, [currentValue, savedValue]);

  function handleGenerateAi() {
    setAiLoading(true);
    if (onGenerateAi) {
      onGenerateAi()
        .then((result) => {
          editorRef.current?.update(result);
          setSavedValue(result);
          setCurrentValue(result);
        })
        .catch((error) => {
          toast(
            ToastType.ERROR,
            "Something went wrong generating the summary."
          );
          notify(error);
        })
        .finally(() => setAiLoading(false));
    }
  }

  function handleSave() {
    if (!currentValue) return;
    setIsSaving(true);
    if (onSave) {
      onSave(currentValue)
        .then((success) => {
          if (success) {
            setSavedValue(currentValue);
            toast(ToastType.SUCCESS, "Summary saved.");
          }
        })
        .catch((error) => {
          toast(
            ToastType.ERROR,
            "Failed to save summary. We've been notified."
          );
          notify(error);
        })
        .finally(() => setIsSaving(false));
    }
  }

  const showEmpty = !defaultValue && readonly;

  return (
    <Flex direction="down" gap="sm">
      <Flex direction="right" justify="between" align="center">
        <Text heading muted size={print ? "sm" : "md"}>
          {title}
        </Text>
        {!readonly && (
          <Flex align="center" gap="xs">
            <Button
              size="sm"
              variant="ghost"
              className="text-primary hover:text-primary"
              onClick={handleGenerateAi}
              disabled={aiLoading}
            >
              <SparklesIcon size={20} />
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || aiLoading || isSaving}
            >
              <Text size="sm">Save changes</Text>
            </Button>
          </Flex>
        )}
      </Flex>

      {showEmpty ? (
        <Flex
          className="border-gray h-[200px] w-full rounded-lg border bg-muted/50"
          justify="center"
          align="center"
        >
          <Text muted>
            A summary has not yet been generated for this player.
          </Text>
        </Flex>
      ) : (
        <div className="relative">
          {aiLoading && (
            <Flex
              className="absolute z-10 h-full w-full rounded-md border bg-white"
              justify="center"
              align="center"
            >
              <SparklesIcon
                size={50}
                className="animate-pulse text-secondary"
              />
            </Flex>
          )}
          <TextEditor
            className={cn(
              "h-[260px]",
              size == "md" && "h-[350px]",
              size == "lg" && "h-[500px]",
              className
            )}
            placeholder="Generate or write a summary..."
            defaultValue={defaultValue}
            readonly={readonly}
            maxLength={readonly ? undefined : maxLength}
            ref={editorRef}
            onChange={(value) => {
              if (value) {
                setCurrentValue(value);
              }
            }}
          />
        </div>
      )}
    </Flex>
  );
}
